<template>
	<div class="inline-outside-report-upload" @click.stop>
		<div class="inline-outside-report-upload__row">
			<div class="inline-outside-report-upload__summary">
				<span>外院报告</span>
				<span class="inline-outside-report-upload__count" :class="{ 'is-empty': !reportCount }">{{ reportCount }}<span class="inline-outside-report-upload__unit">份</span></span>
			</div>
			<van-button class="inline-outside-report-upload__button" native-type="button" icon="plus" color="#5f5aa3" :loading="uploading" :disabled="uploading" @click.stop="openForm">上传</van-button>
		</div>
		<van-popup v-model="formVisible" position="bottom" round :close-on-click-overlay="!uploading" :closeable="!uploading" class="outside-report-form" get-container="body">
			<h3>上传外院报告</h3>
			<van-form @submit="uploadReports">
				<van-field :value="inspectionTime" label="检验时间" placeholder="请选择检验时间" readonly required is-link :disabled="uploading" :rules="[{ required: true, message: '请选择检验时间' }]" @click="openDatePicker" />
				<van-field :value="inspectionHospital" label="检验医院" placeholder="请选择检验医院" readonly required is-link :disabled="uploading" :rules="[{ required: true, message: '请选择检验医院' }]" @click="openHospitalPicker" />
				<div class="outside-report-form__files">
					<div>报告图片（JPG/PNG，单张不超过 10MB）</div>
					<van-uploader v-model="fileList" accept=".jpg,.jpeg,.png,image/jpeg,image/png" multiple :max-count="maxSelectableCount" :before-read="beforeRead" :before-delete="beforeDelete" :disabled="uploading" />
				</div>
				<van-button block round color="#5f5aa3" native-type="submit" :loading="uploading" loading-text="上传中...">确认上传</van-button>
			</van-form>
		</van-popup>
		<van-popup v-model="dateVisible" position="bottom" round get-container="body">
			<van-datetime-picker v-model="selectedDate" type="date" title="检验时间" :min-date="minDate" :max-date="maxDate" @confirm="confirmDate" @cancel="dateVisible = false" />
		</van-popup>
		<van-popup v-model="hospitalVisible" position="bottom" round get-container="body">
			<van-picker v-if="hospitalVisible" show-toolbar title="检验医院" :columns="hospitalColumns" :default-index="selectedHospitalIndex" @confirm="confirmHospital" @cancel="hospitalVisible = false" />
		</van-popup>
	</div>
</template>

<script>
import api from "@/request/api";
import dayjs from "dayjs";
import { Notify } from "vant";

export default {
	name: "InlineOutsideReportUpload",
	props: {
		patient: {
			type: Object,
			default: () => ({}),
		},
		hospitalOptions: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			fileList: [],
			uploading: false,
			formVisible: false,
			dateVisible: false,
			hospitalVisible: false,
			inspectionTime: "",
			inspectionHospital: "",
			selectedDate: new Date(),
			minDate: new Date(1900, 0, 1),
			maxDate: new Date(2099, 11, 31),
		};
	},
	computed: {
		hospitalColumns() {
			return this.hospitalOptions
				.filter(item => item && typeof item.text === 'string' && item.text.trim())
				.map(item => ({ ...item, text: item.text.trim() }));
		},
		selectedHospitalIndex() {
			return Math.max(0, this.hospitalColumns.findIndex(item => item.text === this.inspectionHospital));
		},
		reportCount() {
			const count = Number(this.patient.outsideHospitalReportCount);
			return Number.isFinite(count) && count >= 0 ? Math.floor(count) : 0;
		},
		maxSelectableCount() {
			return 9;
		},
	},
	methods: {
		openForm() {
			if (!this.patient.jzlsh) {
				Notify({ type: 'warning', message: '该住院记录缺少就诊流水号，无法上传' });
				return;
			}
			this.formVisible = true;
		},
		openDatePicker() {
			if (!this.uploading) this.dateVisible = true;
		},
		confirmDate(value) {
			this.inspectionTime = dayjs(value).format('YYYY-MM-DD');
			this.dateVisible = false;
		},
		openHospitalPicker() {
			if (this.uploading) return;
			if (!this.hospitalColumns.length) {
				Notify({ type: 'warning', message: '暂无可选医院，请稍后重试' });
				return;
			}
			this.hospitalVisible = true;
		},
		confirmHospital(option) {
			if (option && !this.uploading) {
				// 接口 inspectionHospital 保存名称，不传病区字典编码。
				this.inspectionHospital = option.text;
			}
			this.hospitalVisible = false;
		},
		beforeDelete(item) {
			return !this.uploading && item.status !== 'done';
		},
		beforeRead(file) {
			const files = Array.isArray(file) ? file : [file];
			if (!this.patient.jzlsh) {
				Notify({ type: "danger", message: "该住院记录缺少就诊流水号，无法上传" });
				return false;
			}
			const isSupportedType = files.every((item) => {
				return ["image/jpeg", "image/png"].indexOf(item.type) !== -1 || /\.(jpe?g|png)$/i.test(item.name || "");
			});
			if (!isSupportedType) {
				Notify({ type: "warning", message: "仅支持 JPG、JPEG、PNG 格式" });
				return false;
			}
			const isWithinSize = files.every((item) => item.size <= 10 * 1024 * 1024);
			if (!isWithinSize) {
				Notify({ type: "warning", message: "单张报告图片不能超过 10MB" });
				return false;
			}
			return true;
		},
		async uploadReports() {
			if (this.uploading) return;
			const files = this.fileList.filter(item => item.status !== 'done');
			if (!this.inspectionTime || !this.inspectionHospital.trim() || !files.length) {
				Notify({ type: 'warning', message: '请填写检验时间、检验医院并选择待上传图片' });
				return;
			}
			if (!this.hospitalColumns.some(item => item.text === this.inspectionHospital)) {
				Notify({ type: 'warning', message: '请重新选择检验医院' });
				return;
			}
			if (files.length > 9) {
				Notify({ type: 'warning', message: '每份外院报告最多上传9张图片，请减少所选图片' });
				return;
			}
			const jzlsh = this.patient.jzlsh;
			// 界面仅选日期，提交时保持后端要求的完整时间格式。
			const inspectionTime = `${this.inspectionTime} 00:00:00`;
			const inspectionHospital = this.inspectionHospital.trim();
			this.uploading = true;
			files.forEach((item) => {
				this.$set(item, "status", "uploading");
				this.$set(item, "message", "上传中...");
			});

			try {
				const res = await api.uploadOutsideHospitalReport(files.map(item => item.file), jzlsh, inspectionTime, inspectionHospital);
				if (!res || !res.success) throw new Error((res && res.message) || "上传失败");
				files.forEach((item) => {
					this.$set(item, "status", "done");
					this.$set(item, "message", "上传成功");
				});
				this.$emit('uploaded', { jzlsh, count: 1 });
				Notify({ type: "success", message: `成功上传 1 份外院报告（${files.length} 张图片）` });
				this.formVisible = false;
			} catch (error) {
				files.forEach((item) => {
					this.$set(item, "status", "failed");
					this.$set(item, "message", (error && error.message) || "上传失败");
				});
				Notify({ type: "danger", message: (error && error.message) || "上传失败，请重试" });
			} finally {
				this.uploading = false;
			}
		},
	},
};
</script>

<style lang="less" scoped>
.outside-report-form {
	box-sizing: border-box;
	max-height: 85vh;
	padding: 0.8rem 0.64rem calc(0.8rem + env(safe-area-inset-bottom));
	overflow-y: auto;
	h3 { margin: 0 0 0.6rem; font-size: 0.853rem; text-align: center; }
	/deep/ .van-field {
		align-items: center;
		min-height: 2.667rem;
		padding: 0.533rem 0.64rem;
		font-size: 0.747rem;
		line-height: 1.12rem;
	}
	/deep/ .van-field__label {
		width: 4.5em;
		margin-right: 0.533rem;
	}
	/deep/ .van-field__control {
		font-size: inherit;
	}
	/deep/ .van-field__control::placeholder {
		color: #969799;
	}
	/deep/ .van-cell__right-icon {
		font-size: 0.747rem;
		line-height: 1.12rem;
	}
	/deep/ .van-field__error-message {
		font-size: 0.64rem;
		line-height: 0.96rem;
	}
	&__files { padding: 0.6rem 0; font-size: 0.64rem; color: #777; }
	&__files > div:first-child { margin-bottom: 0.4rem; }
}
.inline-outside-report-upload {
	width: 100%;
	box-sizing: border-box;

	&__row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		flex-wrap: wrap;
		gap: 0.48rem;
		margin-top: 0.533rem;
		padding-top: 0.533rem;
		border-top: 1px solid #efedf5;
		font-size: 0.747rem;
		font-weight: 400;
		line-height: 1.04rem;
		color: #555;
	}

	&__summary {
		display: flex;
		align-items: center;
		gap: 0.373rem;
	}

	&__count {
		display: inline-flex;
		align-items: baseline;
		gap: 0.133rem;
		padding: 0.107rem 0.32rem;
		border-radius: 0.267rem;
		font-weight: 600;
		font-variant-numeric: tabular-nums;
	}

	&__unit {
		font-size: 0.587rem;
		font-weight: 400;
	}

	&__button {
		flex-shrink: 0;
		height: 1.6rem;
		min-width: 3.2rem;
		padding: 0 0.587rem;
		font-size: 0.693rem;
		font-weight: 500;
		line-height: 1;
		border-radius: 0.8rem;
		/deep/ .van-icon {
			font-size: 0.64rem;
		}
	}
}
</style>
